import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Delta catch-up sync used on socket reconnect (June-2026).
 *
 * Instead of reloading the entire thread when the socket reconnects, we fetch
 * ONLY the messages newer than the last one we already hold (`?after=<ts>`)
 * and merge them by id. This is light + fast and never wipes optimistic /
 * pending bubbles.
 *
 * Returns:
 *   true  → delta sync ran (thread had a known last message)
 *   false → thread was empty / no anchor, caller should do a full load instead
 */
export async function deltaSyncMessages({ roomId, userId, getMessages, setMessages }) {
  if (!roomId) return false;
  const current = (getMessages && getMessages()) || [];

  // Anchor on the newest NON-pending message we already have.
  const anchors = current
    .filter((m) => m && !m.pending && m.timestamp)
    .map((m) => m.timestamp)
    .sort();
  const lastTs = anchors.length ? anchors[anchors.length - 1] : null;
  if (!lastTs) return false; // empty thread → let caller full-load

  try {
    const params = { after: lastTs };
    if (userId) params.user_id = userId;
    const res = await axios.get(`${API}/chat/messages/${roomId}`, { params });
    const delta = Array.isArray(res.data) ? res.data : [];
    if (delta.length === 0) return true; // nothing missed

    setMessages((prev) => {
      const byId = new Map((prev || []).map((m) => [m.id, m]));
      for (const m of delta) {
        // Server doc wins for real messages; keep any optimistic still pending.
        byId.set(m.id, { ...(byId.get(m.id) || {}), ...m });
      }
      // Drop optimistic bubbles whose real doc just arrived (matched by
      // client_message_id) so we don't show duplicates.
      const realCmids = new Set(delta.map((m) => m.client_message_id).filter(Boolean));
      const merged = Array.from(byId.values()).filter(
        (m) => !(m.pending && realCmids.has(m.client_message_id))
      );
      merged.sort((a, b) => String(a.timestamp || '').localeCompare(String(b.timestamp || '')));
      return merged;
    });
    return true;
  } catch (_) {
    return false; // network hiccup → caller may fall back to full load
  }
}
